import React from 'react';
import Image from '@ecs/tng-common/components/image/Image';
import { returnDivider } from '../ByLine';
import { ifAuthorHasProfileImage } from '../../helpers/author-profile-images';
import { imageWithPrefix } from '@ecs/tng-common/scripts/helpers';
import { truncateContent, filterContent } from '../../helpers/functions';

export interface IExpertCardProps {
  id: string;
  description: React.ReactNode;
  heading?: string;
  name?: string;
  title?: string;
  creds?: string;
  imgUrl?: string;
  alt?: string;
  slug?: string;
}

interface IExpertCardState {
  expanded: boolean;
}

export default class MeetExpert extends React.Component<IExpertCardProps, IExpertCardState> {
  state: IExpertCardState = { expanded: false };
  maxLength = 500;
  buffer = 10;

  toggleExpand = () => {
    this.setState((prev) => ({ expanded: !prev.expanded }));
  };

  render() {
    const {
      heading = 'Meet the Expert',
      name,
      title,
      creds,
      description = '',
      alt,
      slug,
      imgUrl: img,
      id
    } = this.props;
    const { expanded } = this.state;

    const { truncatedContent, isTruncated, reconstructFullContent } = truncateContent(
      description,
      this.maxLength,
      this.buffer
    );

    const showMoreBtn = (
      <button
        type="button"
        onClick={this.toggleExpand}
        className={`btn-link h6 mb-0 mt-3 btn d-inline-flex align-items-center text-secondary ${expanded ? 'collapsed-open' : 'collapsed-closed'}`}
        aria-expanded={expanded}
        aria-controls={id}>
        {!expanded ? (
          <>
            View more <span className="ecs-font-icon-nav-angle-down font-icon-size-md" />
          </>
        ) : (
          <>
            View less <span className="ecs-font-icon-nav-angle-up font-icon-size-md" />
          </>
        )}
      </button>
    );

    const profileImage = (src: string) => {
      return (
        <Image
          src={src}
          alt={alt}
          width="120"
          height="120"
          className="meet-expert-block-img rounded-circle me-3 me-lg-4 img-fluid spot-icon-lg"
          lazyLoading
        />
      );
    };

    const renderContent = () => {
      const rawFullContent = reconstructFullContent();
      const fullContent = filterContent(rawFullContent);

      // Combine truncated and remaining content dynamically
      const combinedContent = expanded ? fullContent : truncatedContent;
      const combinedContentArray = Array.isArray(combinedContent)
        ? combinedContent
        : [combinedContent];
      const filteredContent = filterContent(combinedContentArray || []);

      return (
        <>
          {Array.isArray(filteredContent) &&
            filteredContent.map((node, index) => {
              const isLastNode = index === filteredContent.length - 1;
              let className = '';

              if (isLastNode) {
                className = 'mb-0';
              }

              return (
                <React.Fragment key={index}>
                  {node &&
                    (React.isValidElement(node) ? (
                      React.cloneElement(node as React.ReactElement, {
                        className:
                          `${(node as React.ReactElement).props.className || ''} ${className}`.trim()
                      })
                    ) : typeof node === 'string' ? (
                      <p className={className}>{node}</p>
                    ) : null)}
                </React.Fragment>
              );
            })}
        </>
      );
    };

    return (
      <div className="meet-expert-block my-6 my-lg-7">
        <div
          className={`rounded bg-coconut border-0 p-3 p-lg-4 h-100${expanded ? ' expanded' : ''}`}>
          {heading && <p className="h4 mb-4">{heading}</p>}
          {name && (
            <div className="d-flex align-items-start mb-4">
              {slug &&
                ifAuthorHasProfileImage(slug) &&
                profileImage(imageWithPrefix(`/profiles/${slug}.webp`))}

              {img && profileImage(img)}

              <div className="align-self-center">
                <div className="fw-semibold text-dark mb-2">{name}</div>
                <div className="small text-gray mb-0">
                  {title && <span>{title}</span>}
                  {title && creds && returnDivider()}
                  {creds && <span>{creds}</span>}
                </div>
              </div>
            </div>
          )}
          <div
            className="text-dark"
            id={id}>
            {renderContent()}
            {isTruncated && showMoreBtn}
          </div>
        </div>
      </div>
    );
  }
}
